import { cva, type VariantProps } from 'class-variance-authority'
import { AlertCircle, CornerDownLeft } from 'lucide-react'
import * as React from 'react'

import { cn } from '../lib/utils'

// The border color and focus ring live in the `state` variant so the field always reads
// as one cohesive color. The border carries the tone at rest; the ring appears only on
// focus (so every state has a distinct, perceivable focus affordance — WCAG 2.4.7) and
// echoes the field's tone. The ring uses color-mix as an arbitrary value because the
// es-* tokens are bare var() with no <alpha-value>, so the `ring-red/opacity`
// modifier would silently fail and fall back to Tailwind's default blue ring.
const textFieldVariants = cva(
  'flex items-center gap-2.5 w-full min-h-12 rounded-xl border-[1.5px] bg-card px-3.5 py-1 shadow-card transition-colors focus-within:ring-4',
  {
    variants: {
      state: {
        default: 'border-border focus-within:border-brand-green-border focus-within:ring-brand-green-border/[0.14]',
        invalid: 'border-red focus-within:ring-[color-mix(in_srgb,oklch(var(--red))_14%,transparent)]',
        success: 'border-brand-green-border focus-within:ring-brand-green-border/20',
        warning: 'border-amber focus-within:ring-[color-mix(in_srgb,oklch(var(--amber))_14%,transparent)]'
      }
    },
    defaultVariants: { state: 'default' }
  }
)

export interface TextFieldProps extends Omit<React.ComponentProps<'input'>, 'size'>, VariantProps<typeof textFieldVariants> {
  /** Leading adornment (e.g. a search or mail icon). Sized/colored to match the field automatically. */
  leftIcon?: React.ReactNode
  /** Trailing adornment (e.g. a clear button, a unit, a status icon). */
  rightIcon?: React.ReactNode
  /** Extra classes for the outer field wrapper (border, ring, background live here). */
  className?: string
  /** Extra classes for the inner `<input>` element. */
  inputClassName?: string
  /**
   * For a field that saves on Enter: the screen-reader instruction (e.g. "Press Enter to save").
   * Also labels the phone keyboard's return key "Done". Already-translated.
   */
  enterHint?: string
  /**
   * Show the ↵ keycap in the trailing slot. Pointer devices only — touch keyboards label their
   * own return key. Typically `true` once there's a change to save.
   */
  showEnterHint?: boolean
  /**
   * Validation message shown under the field. Already-translated. Sets the invalid tone and wires
   * `aria-describedby`. Renders as a sibling of the field, so place the field in a block container.
   */
  error?: string
}

// The app's standard text field. A bordered card surface whose border + focus ring carry
// the field's `state` — the styling standardized from IngredientCombobox. The treatment
// lives on a wrapper (via focus-within) so leading/trailing adornments sit inside the
// same framed field and the ring wraps the whole thing, not just the bare input.
const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({ className, inputClassName, leftIcon, rightIcon, state, disabled, enterHint, showEnterHint = false, error, ...props }, ref) => {
    const enterHintId = React.useId()
    const errorId = React.useId()
    const tone = error ? 'invalid' : state
    const describedBy = cn(props['aria-describedby'], enterHint && enterHintId, error && errorId) || undefined
    // A fragment, not a wrapper: callers put layout classes on `className`, which must stay on the frame.
    return (
      <>
        <div className={cn(textFieldVariants({ state: tone }), disabled && 'cursor-not-allowed opacity-50', className)}>
          {leftIcon && <span className="flex-shrink-0 text-faint [&_svg]:w-[18px] [&_svg]:h-[18px]">{leftIcon}</span>}
          <input
            ref={ref}
            disabled={disabled}
            className={cn(
              // 16px on mobile so iOS Safari doesn't zoom the viewport on focus (it does for
              // any input < 16px); back to the smaller text-sm design size at md+ where that's moot.
              // Mirrors the bare Input component's text-base md:text-sm approach.
              'flex-1 min-w-0 bg-transparent text-base md:text-sm text-foreground outline-none placeholder:text-faint disabled:cursor-not-allowed',
              inputClassName
            )}
            enterKeyHint={enterHint ? 'done' : undefined}
            {...props}
            aria-describedby={describedBy}
            aria-invalid={props['aria-invalid'] ?? (tone === 'invalid' || undefined)}
          />
          {rightIcon && <span className="flex-shrink-0 text-faint [&_svg]:w-[18px] [&_svg]:h-[18px]">{rightIcon}</span>}
          {enterHint && (
            <span id={enterHintId} className="sr-only">
              {enterHint}
            </span>
          )}
          {enterHint && showEnterHint && (
            <span
              aria-hidden="true"
              className="pointer-events-none hidden size-5 flex-shrink-0 place-items-center rounded-md border border-border text-faint [@media(hover:hover)]:grid">
              <CornerDownLeft size={11} />
            </span>
          )}
        </div>
        {error && (
          <p id={errorId} role="alert" className="mt-2 flex items-center gap-1.5 text-xs font-medium text-red-700 dark:text-red-300">
            <AlertCircle aria-hidden className="size-[13px] shrink-0" strokeWidth={2.4} />
            <span>{error}</span>
          </p>
        )}
      </>
    )
  }
)
TextField.displayName = 'TextField'

export { TextField, textFieldVariants }
